<div class="role23-form-container">
  <p-toast></p-toast>
  <app-sheet-loader [visible]="isLoading" />

  <p-card [header]="formTitle" [subheader]="isDev ? schema.label : ''" *ngIf="!isLoading && schema && form" class="form-card">

    <form [formGroup]="form">
      <!-- Section 1: Disclosure TextBlock (only first section before tables) -->
      <ng-container *ngFor="let control of schema.controls; let controlIndex = index">
        <!-- Only show the first Level 2 header and TextBlock before the tables start -->
        <ng-container *ngIf="controlIndex < 2">
          <!-- Section Headers (Level 2) - First section only -->
          <div *ngIf="control.type === 'SectionHeader' && control.ui.level === 2" class="bg-gray-200 text-black-700 text-sm font-medium px-2 py-1 mt-3">
            {{ control.label }}
          </div>

          <!-- TextBlock Fields - First section only.
               Rich-text (WYSIWYG) field — same preview + pop-out editor as role
               750000 (AS-897 rollout). The control value stays a plain HTML
               string; required parity holds because the field normalizes empty
               content to '' (see RichTextFieldComponent / rich-text-html.util),
               which both Validators.required and the pure validator's isEmptyCell
               treat as empty. -->
          <div class="form-field" *ngIf="control.type === 'TextBlock' && shouldDisplayField(control)">
            <label class="field-label">
              {{ control.label }}<app-required-marker [when]="isFieldRequired(control)" />
            </label>

            <div class="field-input-wrapper">
              <app-rich-text-field
                [formControlName]="control.qname"
                [title]="control.label"
                [placeholder]="control | placeholder: 'Click to add content…'"
                [invalid]="!!(getControl(control.qname)?.invalid && getControl(control.qname)?.touched)"
              ></app-rich-text-field>

              <small class="error-message"
                     *ngIf="getControl(control.qname)?.invalid && getControl(control.qname)?.touched">
                <span *ngIf="getControl(control.qname)?.errors?.['required']">
                  This is a mandatory field.
                </span>
              </small>
            </div>
          </div>
        </ng-container>
      </ng-container>

      <!-- Section 2: Consolidated Table (only show if Nature of Statements is Consolidated) -->
      <div class="financial-statement-table-wrapper mt-4 viewport-sticky" *ngIf="showConsolidatedTable">
        <table
          class="financial-statement-table"
          appFieldLinkTarget
          fieldLinkSheet="notes-issued-capital"
          [fieldLinkControls]="schema.controls"
          [fieldLinkSections]="consolidatedSectionSet"
        >
          <thead>
            <!-- Row 1: Consolidation type (spans amount columns) + fixed columns -->
            <tr>
              <th class="description-column" rowspan="3">Share Capital</th>
              <th class="amount-column merged-header" colspan="4">Consolidated</th>
              <th class="actions-column" rowspan="3">Actions</th>
              <th class="validation-status-column" rowspan="3">Mapping Status</th>
              <th class="validation-error-column" rowspan="3">Error Description</th>
            </tr>
            <!-- Row 2: Financial-period date range -->
            <tr>
              <th class="amount-column merged-header" colspan="4">
                <div>{{ periodHeaderCurrent }}</div>
                <div>(Current)</div>
              </th>
            </tr>
            <!-- Row 3: Individual column headers -->
            <tr>
              <th class="amount-column">Ordinary<br/>shares</th>
              <th class="amount-column">Redeemable<br/>preference<br/>shares</th>
              <th class="amount-column">Non-redeemable<br/>preference<br/>shares</th>
              <th class="amount-column total-column">Total</th>
            </tr>
          </thead>
          <tbody>
            <ng-container *ngFor="let control of schema.controls; let controlIndex = index">
              <!-- Level 2 Section Headers (skip the first one shown above; top of hierarchy — not collapsible) -->
              <tr *ngIf="control.type === 'SectionHeader' && control.ui.level === 2 && controlIndex > 1" class="header-level-2">
                <td colspan="8" class="section-header">
                  {{ control.label }}
                </td>
              </tr>

              <!-- Level 3 Section Headers (expand/collapse toggle) -->
              <tr *ngIf="control.type === 'SectionHeader' && control.ui.level === 3 && shouldShowRowConsolidated(control, controlIndex)" class="header-level-3" (click)="toggleSectionConsolidated(control.qname)">
                <td colspan="8" class="section-header">
                  <i class="pi" [ngClass]="isSectionExpandedConsolidated(control.qname) ? 'pi-chevron-down' : 'pi-chevron-right'" style="margin-right: 0.5rem;"></i>
                  {{ control.label }}
                </td>
              </tr>

              <!-- Level 4 Section Headers (with expand/collapse toggle) - Consolidated -->
              <tr *ngIf="control.type === 'SectionHeader' && control.ui.level === 4 && shouldShowRowConsolidated(control, controlIndex)" class="header-level-4" (click)="toggleSectionConsolidated(control.qname)">
                <td colspan="8" class="section-header">
                  <i class="pi" [ngClass]="isSectionExpandedConsolidated(control.qname) ? 'pi-chevron-down' : 'pi-chevron-right'" style="margin-right: 0.5rem;"></i>
                  {{ control.label }}
                </td>
              </tr>

              <!-- Level 5 Section Headers (with expand/collapse toggle) - Consolidated -->
              <tr *ngIf="control.type === 'SectionHeader' && control.ui.level === 5 && shouldShowRowConsolidated(control, controlIndex)" class="header-level-5" (click)="toggleSectionConsolidated(control.qname)">
                <td colspan="8" class="section-header">
                  <i class="pi" [ngClass]="isSectionExpandedConsolidated(control.qname) ? 'pi-chevron-down' : 'pi-chevron-right'" style="margin-right: 0.5rem;"></i>
                  {{ control.label }}
                </td>
              </tr>

              <!-- Table Column Fields (data rows) - Consolidated -->
              <tr *ngIf="control.ui.tableColumn && shouldShowRowConsolidated(control, controlIndex)"
                  class="data-row"
                  [attr.data-qname]="control.qname"
                  [class.row-has-error]="rowHasBlockingError(control, 'Consolidated')">
                <!-- Description Column with Clickable Link -->
                <td class="description-column">
                  <app-field-link-label [control]="control" /><app-required-marker [when]="isFieldRequired(control)" />
                </td>

                <!-- Consolidated - Ordinary shares -->
                <td class="amount-column">
                  <p-inputnumber
                    appSchemaNumeric
                    appNegativeHighlight
                    [schemaControl]="control"
                    [formControl]="$any(form.get(getXbrlControlName(control.qname, 'Consolidated', 'Ordinary')))"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                    [ngClass]="{'has-required-error': hasColumnError(control, 'Consolidated', 'Ordinary')}"
                  ></p-inputnumber>
                </td>

                <!-- Consolidated - Redeemable preference shares -->
                <td class="amount-column">
                  <p-inputnumber
                    appSchemaNumeric
                    appNegativeHighlight
                    [schemaControl]="control"
                    [formControl]="$any(form.get(getXbrlControlName(control.qname, 'Consolidated', 'Redeemable')))"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                    [ngClass]="{'has-required-error': hasColumnError(control, 'Consolidated', 'Redeemable')}"
                  ></p-inputnumber>
                </td>

                <!-- Consolidated - Non-redeemable preference shares -->
                <td class="amount-column">
                  <p-inputnumber
                    appSchemaNumeric
                    appNegativeHighlight
                    [schemaControl]="control"
                    [formControl]="$any(form.get(getXbrlControlName(control.qname, 'Consolidated', 'NonRedeemable')))"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                    [ngClass]="{'has-required-error': hasColumnError(control, 'Consolidated', 'NonRedeemable')}"
                  ></p-inputnumber>
                </td>

                <!-- Consolidated - Total (User-editable) -->
                <td class="amount-column total-column">
                  <p-inputnumber
                    appSchemaNumeric
                    appNegativeHighlight
                    [schemaControl]="control"
                    [formControl]="$any(form.get(getXbrlControlName(control.qname, 'Consolidated', 'Total')))"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                    [ngClass]="{'has-required-error': hasColumnError(control, 'Consolidated', 'Total')}"
                  ></p-inputnumber>
                </td>

                <!-- Actions Column with SVG Icons -->
                <td class="actions-column">
                  <ng-container *ngIf="!control.ui.calculated && !control.ui.readonly">
                    <!-- Pencil Icon (Not in edit mode) -->
                    <svg *ngIf="isValidationComplete && !isRowInEditMode(control, 'Consolidated')"
                         class="action-icon pencil-icon"
                         viewBox="0 0 16 16"
                         fill="none"
                         xmlns="http://www.w3.org/2000/svg"
                         (click)="enterEditMode(control, 'Consolidated')"
                         title="Edit">
                      <path fill-rule="evenodd" clip-rule="evenodd"
                        d="M12.1657 1.94834C12.3339 1.77238 12.5683 1.67267 12.8136 1.67017C13.0589 1.66766 13.2953 1.7625 13.4671 1.93433L14.0657 2.53292C14.2375 2.70475 14.3324 2.94113 14.3299 3.18647C14.3273 3.4318 14.2276 3.66616 14.0517 3.83434L5.65767 12.2283C5.59027 12.2957 5.50782 12.3462 5.41732 12.3756L2.41732 13.3756C2.28547 13.4187 2.14228 13.4079 2.01828 13.3455C1.89428 13.2831 1.79929 13.1744 1.75443 13.0433C1.70958 12.9122 1.71867 12.7684 1.78 12.6433L2.78 9.6433C2.80936 9.55279 2.85989 9.47034 2.92732 9.40292L11.3157 0.991921C11.3246 0.982976 11.3336 0.974305 11.3426 0.965931C11.6517 0.656721 12.0846 0.512921 12.5207 0.567721C12.4906 0.542121 12.4595 0.517921 12.4276 0.495421C12.3956 0.472921 12.3627 0.451721 12.3292 0.431721C12.2956 0.411721 12.2614 0.392921 12.2267 0.375421C12.192 0.357921 12.1569 0.341721 12.1213 0.326721C12.0857 0.311721 12.0497 0.298121 12.0134 0.285721C11.9771 0.273321 11.9404 0.262221 11.9035 0.252421C11.8666 0.242621 11.8294 0.234121 11.7921 0.226921C11.7547 0.219721 11.7173 0.213721 11.6798 0.208921C11.6422 0.204121 11.6046 0.200621 11.5669 0.198421C11.5292 0.196221 11.4915 0.195321 11.4538 0.195721C11.4161 0.196121 11.3784 0.197821 11.3408 0.200721C11.3031 0.203621 11.2656 0.207721 11.2282 0.213121C11.1908 0.218521 11.1535 0.225121 11.1164 0.232921C11.0793 0.240721 11.0423 0.249721 11.0056 0.259921C10.9689 0.270121 10.9324 0.281421 10.8962 0.293821C10.86 0.306221 10.824 0.319721 10.7883 0.334221C10.7526 0.348721 10.7172 0.364321 10.6821 0.381021C10.647 0.397721 10.6122 0.415421 10.5778 0.434121C10.5434 0.452821 10.5093 0.472421 10.4756 0.493021C10.4419 0.513621 10.4085 0.535121 10.3756 0.557521C10.3426 0.579921 10.31 0.603221 10.2779 0.627421C10.2458 0.651621 10.2142 0.676721 10.1831 0.702621C10.152 0.728521 10.1214 0.755321 10.0913 0.782921C10.0612 0.810521 10.0316 0.838921 10.0026 0.868121C9.97357 0.897321 9.94507 0.927321 9.91707 0.958121C9.88907 0.988921 9.86157 1.02042 9.83457 1.05242C9.80757 1.08442 9.78107 1.11692 9.75507 1.15042C9.72907 1.18392 9.70357 1.21792 9.67857 1.25242C9.65357 1.28692 9.62907 1.32192 9.60507 1.35742C9.58107 1.39292 9.55757 1.42892 9.53457 1.46542L11.5343 3.46517C11.571 3.44217 11.607 3.41867 11.6425 3.39467C11.678 3.37067 11.7128 3.34617 11.7473 3.32117C11.7818 3.29617 11.8158 3.27067 11.8493 3.24467C11.8828 3.21867 11.9158 3.19217 11.9483 3.16517C11.9808 3.13817 12.0128 3.11067 12.0443 3.08267C12.0758 3.05467 12.1068 3.02617 12.1373 2.99717C12.1678 2.96817 12.1978 2.93867 12.2273 2.90867C12.2568 2.87867 12.2858 2.84817 12.3143 2.81717C12.3428 2.78617 12.3708 2.75467 12.3983 2.72267C12.4258 2.69067 12.4528 2.65817 12.4793 2.62517C12.5058 2.59217 12.5318 2.55867 12.5573 2.52467C12.5828 2.49067 12.6078 2.45617 12.6323 2.42117C12.6568 2.38617 12.6808 2.35067 12.7043 2.31467C12.7278 2.27867 12.7508 2.24217 12.7733 2.20517C12.7958 2.16817 12.8178 2.13067 12.8393 2.09267C12.8608 2.05467 12.8818 2.01617 12.9023 1.97717C12.9228 1.93817 12.9428 1.89867 12.9623 1.85867C12.9818 1.81867 13.0008 1.77817 13.0193 1.73717C13.0378 1.69617 13.0558 1.65467 13.0733 1.61267C13.0908 1.57067 13.1078 1.52817 13.1243 1.48517C13.1408 1.44217 13.1568 1.39867 13.1723 1.35467C13.1878 1.31067 13.2028 1.26617 13.2173 1.22117C13.2318 1.17617 13.2458 1.13067 13.2593 1.08467L12.1657 1.94834ZM3.74732 9.89267L4.62732 11.6533L6.38799 12.5333L13.0517 5.86967L10.1303 2.94817L3.74732 9.89267Z"
                        fill="currentColor" />
                    </svg>

                    <!-- Tick and Cross Icons (In edit mode) -->
                    <div *ngIf="isRowInEditMode(control, 'Consolidated')" class="action-icons-group">
                      <!-- Tick (Save) Icon -->
                      <svg class="action-icon tick-icon"
                           viewBox="0 0 16 16"
                           fill="none"
                           xmlns="http://www.w3.org/2000/svg"
                           (click)="saveEditMode(control, 'Consolidated')"
                           title="Save">
                        <path d="M13.5 3.5L6 11L2.5 7.5"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round" />
                      </svg>

                      <!-- Cross (Cancel) Icon -->
                      <svg class="action-icon cross-icon"
                           viewBox="0 0 16 16"
                           fill="none"
                           xmlns="http://www.w3.org/2000/svg"
                           (click)="cancelEditMode(control, 'Consolidated')"
                           title="Cancel">
                        <path d="M12 4L4 12M4 4L12 12"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round" />
                      </svg>
                    </div>
                  </ng-container>
                </td>

                <!-- Mapping Status -->
                <td
                  class="validation-status-column"
                  [ngClass]="{
                    'status-failed': isValidationComplete && getRowValidationStatus(control, 'Consolidated') === 'Failed',
                    'status-validated-warning': isValidationComplete && getRowValidationStatus(control, 'Consolidated') === 'Validated*'
                  }"
                >
                  <ng-container *ngIf="isValidationComplete; else validatingConsolidated">
                    <ng-container [ngSwitch]="getRowValidationStatus(control, 'Consolidated')">
                      <ng-container *ngSwitchCase="'Validated*'">
                        Validated<span class="status-asterisk">*</span>
                      </ng-container>
                      <ng-container *ngSwitchCase="'Failed'">
                        Failed
                      </ng-container>
                      <ng-container *ngSwitchDefault>
                        {{ getRowValidationStatus(control, 'Consolidated') }}
                      </ng-container>
                    </ng-container>
                  </ng-container>
                  <ng-template #validatingConsolidated>
                    <p-skeleton width="70%" height="1rem" />
                  </ng-template>
                </td>

                <!-- Error Description -->
                <td class="validation-error-column">
                  <ng-container *ngIf="isValidationComplete; else errorLoadingConsolidated">
                    <span [innerHTML]="getRowErrorDescription(control, 'Consolidated')"></span>
                  </ng-container>
                  <ng-template #errorLoadingConsolidated>
                    <p-skeleton width="100%" height="1rem" />
                  </ng-template>
                </td>
              </tr>
            </ng-container>
          </tbody>
        </table>
      </div>

      <!-- Section 3: Separate Table -->
      <div class="financial-statement-table-wrapper mt-4 viewport-sticky">
        <table
          class="financial-statement-table"
          appFieldLinkTarget
          fieldLinkSheet="notes-issued-capital"
          [fieldLinkControls]="schema.controls"
          [fieldLinkSections]="separateSectionSet"
        >
          <thead>
            <!-- Row 1: Consolidation type (spans amount columns) + fixed columns -->
            <tr>
              <th class="description-column" rowspan="3">Share Capital</th>
              <th class="amount-column merged-header" colspan="4">Separate</th>
              <th class="actions-column" rowspan="3">Actions</th>
              <th class="validation-status-column" rowspan="3">Mapping Status</th>
              <th class="validation-error-column" rowspan="3">Error Description</th>
            </tr>
            <!-- Row 2: Financial-period date range -->
            <tr>
              <th class="amount-column merged-header" colspan="4">
                <div>{{ periodHeaderCurrent }}</div>
                <div>(Current)</div>
              </th>
            </tr>
            <!-- Row 3: Individual column headers -->
            <tr>
              <th class="amount-column">Ordinary<br/>shares</th>
              <th class="amount-column">Redeemable<br/>preference<br/>shares</th>
              <th class="amount-column">Non-redeemable<br/>preference<br/>shares</th>
              <th class="amount-column total-column">Total</th>
            </tr>
          </thead>
          <tbody>
            <ng-container *ngFor="let control of schema.controls; let controlIndex = index">
              <!-- Level 2 Section Headers (skip the first one shown above; top of hierarchy — not collapsible) -->
              <tr *ngIf="control.type === 'SectionHeader' && control.ui.level === 2 && controlIndex > 1" class="header-level-2">
                <td colspan="8" class="section-header">
                  {{ control.label }}
                </td>
              </tr>

              <!-- Level 3 Section Headers (expand/collapse toggle) -->
              <tr *ngIf="control.type === 'SectionHeader' && control.ui.level === 3 && shouldShowRowSeparate(control, controlIndex)" class="header-level-3" (click)="toggleSectionSeparate(control.qname)">
                <td colspan="8" class="section-header">
                  <i class="pi" [ngClass]="isSectionExpandedSeparate(control.qname) ? 'pi-chevron-down' : 'pi-chevron-right'" style="margin-right: 0.5rem;"></i>
                  {{ control.label }}
                </td>
              </tr>

              <!-- Level 4 Section Headers (with expand/collapse toggle) - Separate -->
              <tr *ngIf="control.type === 'SectionHeader' && control.ui.level === 4 && shouldShowRowSeparate(control, controlIndex)" class="header-level-4" (click)="toggleSectionSeparate(control.qname)">
                <td colspan="8" class="section-header">
                  <i class="pi" [ngClass]="isSectionExpandedSeparate(control.qname) ? 'pi-chevron-down' : 'pi-chevron-right'" style="margin-right: 0.5rem;"></i>
                  {{ control.label }}
                </td>
              </tr>

              <!-- Level 5 Section Headers (with expand/collapse toggle) - Separate -->
              <tr *ngIf="control.type === 'SectionHeader' && control.ui.level === 5 && shouldShowRowSeparate(control, controlIndex)" class="header-level-5" (click)="toggleSectionSeparate(control.qname)">
                <td colspan="8" class="section-header">
                  <i class="pi" [ngClass]="isSectionExpandedSeparate(control.qname) ? 'pi-chevron-down' : 'pi-chevron-right'" style="margin-right: 0.5rem;"></i>
                  {{ control.label }}
                </td>
              </tr>

              <!-- Table Column Fields (data rows) - Separate -->
              <tr *ngIf="control.ui.tableColumn && shouldShowRowSeparate(control, controlIndex)"
                  class="data-row"
                  [attr.data-qname]="control.qname"
                  [class.row-has-error]="rowHasBlockingError(control, 'Separate')">
                <!-- Description Column with Clickable Link -->
                <td class="description-column">
                  <app-field-link-label [control]="control" /><app-required-marker [when]="isFieldRequired(control)" />
                </td>

                <!-- Separate - Ordinary shares -->
                <td class="amount-column">
                  <p-inputnumber
                    appSchemaNumeric
                    appNegativeHighlight
                    [schemaControl]="control"
                    [formControl]="$any(form.get(getXbrlControlName(control.qname, 'Separate', 'Ordinary')))"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                    [ngClass]="{'has-required-error': hasColumnError(control, 'Separate', 'Ordinary')}"
                  ></p-inputnumber>
                </td>

                <!-- Separate - Redeemable preference shares -->
                <td class="amount-column">
                  <p-inputnumber
                    appSchemaNumeric
                    appNegativeHighlight
                    [schemaControl]="control"
                    [formControl]="$any(form.get(getXbrlControlName(control.qname, 'Separate', 'Redeemable')))"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                    [ngClass]="{'has-required-error': hasColumnError(control, 'Separate', 'Redeemable')}"
                  ></p-inputnumber>
                </td>

                <!-- Separate - Non-redeemable preference shares -->
                <td class="amount-column">
                  <p-inputnumber
                    appSchemaNumeric
                    appNegativeHighlight
                    [schemaControl]="control"
                    [formControl]="$any(form.get(getXbrlControlName(control.qname, 'Separate', 'NonRedeemable')))"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                    [ngClass]="{'has-required-error': hasColumnError(control, 'Separate', 'NonRedeemable')}"
                  ></p-inputnumber>
                </td>

                <!-- Separate - Total (User-editable) -->
                <td class="amount-column total-column">
                  <p-inputnumber
                    appSchemaNumeric
                    appNegativeHighlight
                    [schemaControl]="control"
                    [formControl]="$any(form.get(getXbrlControlName(control.qname, 'Separate', 'Total')))"
                    mode="decimal"
                    [minFractionDigits]="numericPolicy(control).decimals"
                    [maxFractionDigits]="numericPolicy(control).decimals"
                    [useGrouping]="numericPolicy(control).grouping"
                    [style]="{'width': '100%'}"
                    [ngClass]="{'has-required-error': hasColumnError(control, 'Separate', 'Total')}"
                  ></p-inputnumber>
                </td>

                <!-- Actions Column with SVG Icons -->
                <td class="actions-column">
                  <ng-container *ngIf="!control.ui.calculated && !control.ui.readonly">
                    <!-- Pencil Icon (Not in edit mode) -->
                    <svg *ngIf="isValidationComplete && !isRowInEditMode(control, 'Separate')"
                         class="action-icon pencil-icon"
                         viewBox="0 0 16 16"
                         fill="none"
                         xmlns="http://www.w3.org/2000/svg"
                         (click)="enterEditMode(control, 'Separate')"
                         title="Edit">
                      <path fill-rule="evenodd" clip-rule="evenodd"
                        d="M12.1657 1.94834C12.3339 1.77238 12.5683 1.67267 12.8136 1.67017C13.0589 1.66766 13.2953 1.7625 13.4671 1.93433L14.0657 2.53292C14.2375 2.70475 14.3324 2.94113 14.3299 3.18647C14.3273 3.4318 14.2276 3.66616 14.0517 3.83434L5.65767 12.2283C5.59027 12.2957 5.50782 12.3462 5.41732 12.3756L2.41732 13.3756C2.28547 13.4187 2.14228 13.4079 2.01828 13.3455C1.89428 13.2831 1.79929 13.1744 1.75443 13.0433C1.70958 12.9122 1.71867 12.7684 1.78 12.6433L2.78 9.6433C2.80936 9.55279 2.85989 9.47034 2.92732 9.40292L11.3157 0.991921C11.3246 0.982976 11.3336 0.974305 11.3426 0.965931C11.6517 0.656721 12.0846 0.512921 12.5207 0.567721C12.4906 0.542121 12.4595 0.517921 12.4276 0.495421C12.3956 0.472921 12.3627 0.451721 12.3292 0.431721C12.2956 0.411721 12.2614 0.392921 12.2267 0.375421C12.192 0.357921 12.1569 0.341721 12.1213 0.326721C12.0857 0.311721 12.0497 0.298121 12.0134 0.285721C11.9771 0.273321 11.9404 0.262221 11.9035 0.252421C11.8666 0.242621 11.8294 0.234121 11.7921 0.226921C11.7547 0.219721 11.7173 0.213721 11.6798 0.208921C11.6422 0.204121 11.6046 0.200621 11.5669 0.198421C11.5292 0.196221 11.4915 0.195321 11.4538 0.195721C11.4161 0.196121 11.3784 0.197821 11.3408 0.200721C11.3031 0.203621 11.2656 0.207721 11.2282 0.213121C11.1908 0.218521 11.1535 0.225121 11.1164 0.232921C11.0793 0.240721 11.0423 0.249721 11.0056 0.259921C10.9689 0.270121 10.9324 0.281421 10.8962 0.293821C10.86 0.306221 10.824 0.319721 10.7883 0.334221C10.7526 0.348721 10.7172 0.364321 10.6821 0.381021C10.647 0.397721 10.6122 0.415421 10.5778 0.434121C10.5434 0.452821 10.5093 0.472421 10.4756 0.493021C10.4419 0.513621 10.4085 0.535121 10.3756 0.557521C10.3426 0.579921 10.31 0.603221 10.2779 0.627421C10.2458 0.651621 10.2142 0.676721 10.1831 0.702621C10.152 0.728521 10.1214 0.755321 10.0913 0.782921C10.0612 0.810521 10.0316 0.838921 10.0026 0.868121C9.97357 0.897321 9.94507 0.927321 9.91707 0.958121C9.88907 0.988921 9.86157 1.02042 9.83457 1.05242C9.80757 1.08442 9.78107 1.11692 9.75507 1.15042C9.72907 1.18392 9.70357 1.21792 9.67857 1.25242C9.65357 1.28692 9.62907 1.32192 9.60507 1.35742C9.58107 1.39292 9.55757 1.42892 9.53457 1.46542L11.5343 3.46517C11.571 3.44217 11.607 3.41867 11.6425 3.39467C11.678 3.37067 11.7128 3.34617 11.7473 3.32117C11.7818 3.29617 11.8158 3.27067 11.8493 3.24467C11.8828 3.21867 11.9158 3.19217 11.9483 3.16517C11.9808 3.13817 12.0128 3.11067 12.0443 3.08267C12.0758 3.05467 12.1068 3.02617 12.1373 2.99717C12.1678 2.96817 12.1978 2.93867 12.2273 2.90867C12.2568 2.87867 12.2858 2.84817 12.3143 2.81717C12.3428 2.78617 12.3708 2.75467 12.3983 2.72267C12.4258 2.69067 12.4528 2.65817 12.4793 2.62517C12.5058 2.59217 12.5318 2.55867 12.5573 2.52467C12.5828 2.49067 12.6078 2.45617 12.6323 2.42117C12.6568 2.38617 12.6808 2.35067 12.7043 2.31467C12.7278 2.27867 12.7508 2.24217 12.7733 2.20517C12.7958 2.16817 12.8178 2.13067 12.8393 2.09267C12.8608 2.05467 12.8818 2.01617 12.9023 1.97717C12.9228 1.93817 12.9428 1.89867 12.9623 1.85867C12.9818 1.81867 13.0008 1.77817 13.0193 1.73717C13.0378 1.69617 13.0558 1.65467 13.0733 1.61267C13.0908 1.57067 13.1078 1.52817 13.1243 1.48517C13.1408 1.44217 13.1568 1.39867 13.1723 1.35467C13.1878 1.31067 13.2028 1.26617 13.2173 1.22117C13.2318 1.17617 13.2458 1.13067 13.2593 1.08467L12.1657 1.94834ZM3.74732 9.89267L4.62732 11.6533L6.38799 12.5333L13.0517 5.86967L10.1303 2.94817L3.74732 9.89267Z"
                        fill="currentColor" />
                    </svg>

                    <!-- Tick and Cross Icons (In edit mode) -->
                    <div *ngIf="isRowInEditMode(control, 'Separate')" class="action-icons-group">
                      <!-- Tick (Save) Icon -->
                      <svg class="action-icon tick-icon"
                           viewBox="0 0 16 16"
                           fill="none"
                           xmlns="http://www.w3.org/2000/svg"
                           (click)="saveEditMode(control, 'Separate')"
                           title="Save">
                        <path d="M13.5 3.5L6 11L2.5 7.5"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round" />
                      </svg>

                      <!-- Cross (Cancel) Icon -->
                      <svg class="action-icon cross-icon"
                           viewBox="0 0 16 16"
                           fill="none"
                           xmlns="http://www.w3.org/2000/svg"
                           (click)="cancelEditMode(control, 'Separate')"
                           title="Cancel">
                        <path d="M12 4L4 12M4 4L12 12"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round" />
                      </svg>
                    </div>
                  </ng-container>
                </td>

                <!-- Mapping Status -->
                <td
                  class="validation-status-column"
                  [ngClass]="{
                    'status-failed': isValidationComplete && getRowValidationStatus(control, 'Separate') === 'Failed',
                    'status-validated-warning': isValidationComplete && getRowValidationStatus(control, 'Separate') === 'Validated*'
                  }"
                >
                  <ng-container *ngIf="isValidationComplete; else validatingSeparate">
                    <ng-container [ngSwitch]="getRowValidationStatus(control, 'Separate')">
                      <ng-container *ngSwitchCase="'Validated*'">
                        Validated<span class="status-asterisk">*</span>
                      </ng-container>
                      <ng-container *ngSwitchCase="'Failed'">
                        Failed
                      </ng-container>
                      <ng-container *ngSwitchDefault>
                        {{ getRowValidationStatus(control, 'Separate') }}
                      </ng-container>
                    </ng-container>
                  </ng-container>
                  <ng-template #validatingSeparate>
                    <p-skeleton width="70%" height="1rem" />
                  </ng-template>
                </td>

                <!-- Error Description -->
                <td class="validation-error-column">
                  <ng-container *ngIf="isValidationComplete; else errorLoadingSeparate">
                    <span [innerHTML]="getRowErrorDescription(control, 'Separate')"></span>
                  </ng-container>
                  <ng-template #errorLoadingSeparate>
                    <p-skeleton width="100%" height="1rem" />
                  </ng-template>
                </td>
              </tr>
            </ng-container>
          </tbody>
        </table>
      </div>
      <submit-for-validation
        [form]="form"
        [sheetKey]="'notesIssuedCapital'"
        [formTitle]="formTitle"
        [evaluateSheetStatus]="evaluateSheetStatus.bind(this)"
        (cancelConfirmed)="onCancelConfirmed()"
      ></submit-for-validation>
    </form>
  </p-card>
</div>
